import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useCodeViewerContext,
    CodeViewerContext
} from "../../context";
import type {
    ICodeViewerActionsProps
} from "../../type";
import {
    TextWrap as TextWrapIcon
} from "lucide-react-native";
import stylesheet, {
    getCodeActionsHitMargin,
    getCodeActionsInset
} from "../../stylesheet";
import CodeCopyAction from "../../internal/copyAction";
import Toggle from "../../../toggle";
import Icon from "../../../icon";

const CodeViewerWrapIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const context = useContext(CodeViewerContext);

    return <Icon
        customTheme={context?.customTheme}
        customColor={customColor}
        icon={TextWrapIcon}
        color={color}
        size={size}
    />;
};

const CodeViewerActions: FC<ICodeViewerActionsProps> = ({
    isWrapToggleVisible = true,
    children,
    style,
    role,
    ...props
}) => {
    const context = useCodeViewerContext({
        part: "actions"
    });

    const {
        controlHeights,
        touchTargets,
        spaces
    } = NCoreUIKitTheme.useContext(context?.customTheme);

    const pointerType = usePointerType();

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    if(!context) {
        return null;
    }

    const hitMargin = getCodeActionsHitMargin({
        controlHeights,
        touchTargets,
        pointerType
    });

    const renderWrapToggle = () => {
        if(!isWrapToggleVisible) {
            return null;
        }

        return <Toggle
            onPressedChange={context.handleWrappedChange}
            title={localize("code-wrap-lines")}
            customTheme={context.customTheme}
            isPressed={context.isWrapped}
            icon={CodeViewerWrapIcon}
            style={{
                margin: hitMargin
            }}
            isIconOnly={true}
            variant="ghost"
            size="small"
        />;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.headerActions,
            {
                marginVertical: hitMargin,
                gap: spaces.spacingSm
            }
        ]}
        role={role ?? "group"}
    >
        {children}
        {renderWrapToggle()}
        <CodeCopyAction
            customLocalize={context.customLocalize}
            borderRadius={context.borderRadius}
            customTheme={context.customTheme}
            inset={getCodeActionsInset({
                size: context.size,
                controlHeights,
                spaces
            })}
            type={context.type}
            code={context.code}
        />
    </View>;
};
export default Object.assign(CodeViewerActions, {
    codeViewerPart: "actions" as const
});
